import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useUser } from '~/@biz_account/useUser/useUser'
import Analytics from './analytics'
import { addActiveCampaignScriptTag } from './activeCampaign'

type Props = {
  /**
   * Function to get current user
   * Should return user object with _id and profile.sign, or null/undefined
   */
  getUser?: () =>
    | { _id: string; profile?: { sign?: string } }
    | null
    | undefined

  /**
   * Function to get instance name (e.g., 'production', 'staging')
   */
  getInstanceName?: () => string | undefined
}

/**
 * AnalyticsProvider component for React VITE apps
 *
 * Usage:
 *
 * <AnalyticsProvider getUser={() => currentUser}>
 *   <App />
 * </AnalyticsProvider>
 *
 */

function AnalyticsProvider({ getInstanceName }: Props) {
  const { isLoading, user } = useUser()
  const location = useLocation()

  useEffect(() => {
    // Initialize analytics with configuration
    Analytics.init({
      getUser: () => user,
      getRouteName: () => location.pathname,
      getInstanceName,
    })

    addActiveCampaignScriptTag()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Set user in analytics
    if (!isLoading) Analytics.setUser(user ?? null)
    // profile fields feed Meta Advanced Matching via dataLayer (phone arrives after verification)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoading,
    user?._id,
    user?.profile?.email,
    user?.profile?.phone,
    user?.profile?.gdpr?.marketingType,
  ])

  useEffect(() => {
    // Track page changes when route changes
    Analytics.page(location.pathname)
  }, [location.pathname])

  return null
}

export { AnalyticsProvider }
